import React, { useState, useEffect } from 'react';
import { database, ref, set, onValue, auth } from '../firebase';
import { menuData } from './Dashboard';

const flattenMenuData = (menuItems, parentMenu = '') => {
  let flatItems = [];
  menuItems.forEach((item) => {
    const mainMenu = parentMenu || item.label;
    if (item.submenu) {
      item.submenu.forEach((sub) => {
        flatItems.push({
          id: sub.path,
          mainMenu,
          menuName: sub.label,
          path: sub.path,
        });
        if (sub.subItems) {
          flatItems = flatItems.concat(
            sub.subItems.map((subItem) => ({
              id: subItem.path,
              mainMenu,
              menuName: subItem.label,
              path: subItem.path,
            }))
          );
        }
      });
    }
  });
  return flatItems;
};

const menuItems = flattenMenuData(menuData);

const EmployeeRight = () => {
  const [userId, setUserId] = useState('');
  const [userName, setUserName] = useState('');
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [pendingPermissions, setPendingPermissions] = useState({});

  const SUPER_ADMIN_EMAIL = 'saalim@gmail.com';

  // Group menu items by main menu
  const groupedMenus = menuItems.reduce((acc, item) => {
    if (!acc[item.mainMenu]) {
      acc[item.mainMenu] = [];
    }
    acc[item.mainMenu].push(item);
    return acc;
  }, {});

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        if (!auth.currentUser) {
          setError('User not authenticated');
          setLoading(false);
          return;
        }

        const adminEmailKey = auth.currentUser.email.replace(/\./g, '_');
        const employeesRef = ref(database, `users/${adminEmailKey}/employees`);
        onValue(
          employeesRef,
          (snapshot) => {
            const data = snapshot.val();
            const employeesList = data
              ? Object.keys(data)
                  .map((key) => ({
                    id: key,
                    ...data[key],
                  }))
                  .filter((emp) => emp.email !== SUPER_ADMIN_EMAIL)
              : [];
            setEmployees(employeesList);
          },
          { errorCallback: (error) => setError('Failed to fetch employees') }
        );
      } catch (err) {
        setError('Failed to fetch data');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    if (userId) {
      const emailKey = userId.replace(/\./g, '_');
      const rightsRef = ref(database, `employeeRights/${emailKey}`);
      onValue(
        rightsRef,
        (snapshot) => {
          const data = snapshot.val();
          setPendingPermissions(data?.allowedMenus || {});
        },
        { errorCallback: (error) => setError('Failed to fetch permissions') }
      );
    }
  }, [userId]);

  const handleUserIdChange = (e) => {
    const selectedEmail = e.target.value;
    setUserId(selectedEmail);
    const selectedEmployee = employees.find((emp) => emp.email === selectedEmail);
    setUserName(selectedEmployee ? selectedEmployee.name : '');
    setPendingPermissions({});
  };

  const togglePermission = (mainMenu, path) => {
    setPendingPermissions((prev) => {
      const updated = { ...prev };
      if (!updated[mainMenu]) {
        updated[mainMenu] = [];
      }
      if (updated[mainMenu].includes(path)) {
        updated[mainMenu] = updated[mainMenu].filter((p) => p !== path);
      } else {
        updated[mainMenu] = [...updated[mainMenu], path];
      }
      if (updated[mainMenu].length === 0) {
        delete updated[mainMenu];
      }
      return updated;
    });
  };

  // Select All Pages functionality
  const handleSelectAllPages = () => {
    const allPermissions = {};
    Object.entries(groupedMenus).forEach(([mainMenu, items]) => {
      allPermissions[mainMenu] = items.map(item => item.path);
    });
    setPendingPermissions(allPermissions);
  };

  // Deselect All Pages functionality
  const handleDeselectAllPages = () => {
    setPendingPermissions({});
  };

  // Check if all pages are selected
  const areAllPagesSelected = () => {
    const totalPages = menuItems.length;
    const selectedPages = Object.values(pendingPermissions).flat().length;
    return totalPages === selectedPages && totalPages > 0;
  };

  const handleSaveRights = async (e) => {
    e.preventDefault();
    setError('');
    if (!userId) {
      setError('Please select an employee email');
      return;
    }
    if (userId === SUPER_ADMIN_EMAIL) {
      setError('Cannot modify permissions for SuperAdmin');
      return;
    }
    try {
      setLoading(true);
      const emailKey = userId.replace(/\./g, '_');
      const rightsRef = ref(database, `employeeRights/${emailKey}`);
      await set(rightsRef, {
        email: userId,
        userName,
        allowedMenus: pendingPermissions,
      });
      setLoading(false);
      setUserId('');
      setUserName('');
      setPendingPermissions({});
      alert('Employee rights saved successfully!');
    } catch (err) {
      setError('Failed to save rights');
      console.error(err);
      setLoading(false);
    }
  };

  return (
    <div style={{ 
      padding: '20px', 
      fontFamily: 'Inter, Arial, sans-serif', 
      maxWidth: '1400px', 
      margin: '0 auto',
      backgroundColor: '#f8fafc'
    }}>
      <div style={{
        backgroundColor: '#ffffff',
        borderRadius: '12px',
        padding: '24px',
        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.05)',
        border: '1px solid #e2e8f0'
      }}>
        <h2 style={{ 
          color: '#1e293b', 
          marginBottom: '24px',
          fontSize: '24px',
          fontWeight: '600',
          borderBottom: '2px solid #3b82f6',
          paddingBottom: '12px'
        }}>
          Employee Right Assignment
        </h2>

        {error && (
          <div style={{
            color: '#dc2626',
            padding: '12px 16px',
            backgroundColor: '#fef2f2',
            borderRadius: '8px',
            marginBottom: '20px',
            border: '1px solid #fecaca'
          }}>
            {error}
          </div>
        )}

        <form onSubmit={handleSaveRights}>
          {/* Employee Selection */}
          <div style={{
            backgroundColor: '#f8fafc',
            padding: '20px',
            borderRadius: '8px',
            marginBottom: '24px',
            border: '1px solid #e2e8f0'
          }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', alignItems: 'end' }}>
              <div>
                <label style={{ 
                  display: 'block', 
                  marginBottom: '8px', 
                  fontWeight: '600',
                  color: '#374151'
                }}>
                  Select Employee Email *
                </label>
                <select
                  value={userId}
                  onChange={handleUserIdChange}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '2px solid #d1d5db',
                    borderRadius: '8px',
                    boxSizing: 'border-box',
                    fontSize: '14px',
                    backgroundColor: '#ffffff',
                    transition: 'border-color 0.2s',
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                  onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
                  required
                >
                  <option value="">-- Select Employee Email --</option>
                  {employees.map(employee => (
                    <option key={employee.id} value={employee.email}>
                      {employee.email}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label style={{ 
                  display: 'block', 
                  marginBottom: '8px', 
                  fontWeight: '600',
                  color: '#374151'
                }}>
                  Employee Name
                </label>
                <input
                  type="text"
                  value={userName}
                  readOnly
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '8px',
                    boxSizing: 'border-box',
                    backgroundColor: '#f9fafb',
                    cursor: 'not-allowed',
                    fontSize: '14px',
                    color: '#6b7280'
                  }}
                  placeholder="Employee name will appear here"
                />
              </div>
            </div>
          </div>

          {/* Select All Pages Section */}
          {userId && (
            <div style={{
              backgroundColor: '#f8fafc',
              padding: '16px',
              borderRadius: '8px',
              marginBottom: '24px',
              border: '1px solid #e2e8f0',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <span style={{ 
                fontSize: '16px', 
                fontWeight: '600', 
                color: '#374151' 
              }}>
                Page Selection Controls
              </span>
              <div style={{ display: 'flex', gap: '12px' }}>
                <button
                  type="button"
                  onClick={handleSelectAllPages}
                  disabled={areAllPagesSelected()}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: areAllPagesSelected() ? '#9ca3af' : '#10b981',
                    color: '#ffffff',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: areAllPagesSelected() ? 'not-allowed' : 'pointer',
                    fontSize: '14px',
                    fontWeight: '500',
                    transition: 'background-color 0.2s'
                  }}
                >
                  Select All Pages
                </button>
                <button
                  type="button"
                  onClick={handleDeselectAllPages}
                  disabled={Object.keys(pendingPermissions).length === 0}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: Object.keys(pendingPermissions).length === 0 ? '#9ca3af' : '#ef4444',
                    color: '#ffffff',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: Object.keys(pendingPermissions).length === 0 ? 'not-allowed' : 'pointer',
                    fontSize: '14px',
                    fontWeight: '500',
                    transition: 'background-color 0.2s'
                  }}
                >
                  Deselect All Pages
                </button>
              </div>
            </div>
          )}

          {/* Menu Items Section */}
          {userId && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              {Object.entries(groupedMenus).map(([mainMenu, items]) => (
                <div key={mainMenu} style={{
                  backgroundColor: '#ffffff',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  overflow: 'hidden'
                }}>
                  <div style={{
                    backgroundColor: '#f3f4f6',
                    padding: '12px 16px',
                    fontWeight: '600',
                    color: '#374151',
                    borderBottom: '1px solid #e5e7eb'
                  }}>
                    {mainMenu} ({items.length} pages)
                  </div>
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{
                      width: '100%',
                      borderCollapse: 'collapse'
                    }}>
                      <thead>
                        <tr style={{ backgroundColor: '#f8fafc' }}>
                          <th style={{ 
                            padding: '12px', 
                            textAlign: 'left', 
                            fontWeight: '600',
                            color: '#374151',
                            borderBottom: '1px solid #e5e7eb'
                          }}>
                            Page Name
                          </th>
                          <th style={{ 
                            padding: '12px', 
                            textAlign: 'center', 
                            fontWeight: '600',
                            color: '#374151',
                            borderBottom: '1px solid #e5e7eb',
                            minWidth: '100px'
                          }}>
                            View Access
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {items.map((item, index) => (
                          <tr key={item.id} style={{
                            backgroundColor: index % 2 === 0 ? '#ffffff' : '#f9fafb',
                            borderBottom: '1px solid #f3f4f6'
                          }}>
                            <td style={{ 
                              padding: '12px',
                              color: '#374151',
                              fontWeight: '500'
                            }}>
                              {item.menuName}
                            </td>
                            <td style={{ padding: '12px', textAlign: 'center' }}>
                              <label style={{ display: 'inline-flex', alignItems: 'center', cursor: 'pointer' }}>
                                <input
                                  type="checkbox"
                                  checked={pendingPermissions[mainMenu]?.includes(item.path) || false}
                                  onChange={() => togglePermission(mainMenu, item.path)}
                                  style={{
                                    width: '18px',
                                    height: '18px',
                                    accentColor: '#3b82f6',
                                    cursor: 'pointer'
                                  }}
                                />
                              </label>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Save Button */}
          {userId && (
            <div style={{ marginTop: '32px', textAlign: 'center' }}>
              <button
                type="submit"
                style={{
                  padding: '14px 32px',
                  backgroundColor: '#4CAF50',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '16px',
                  fontWeight: '600',
                  minWidth: '200px',
                  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                  transition: 'background-color 0.2s, transform 0.1s'
                }}
                onMouseOver={(e) => {
                  e.target.style.backgroundColor = '#45a049';
                  e.target.style.transform = 'translateY(-1px)';
                }}
                onMouseOut={(e) => {
                  e.target.style.backgroundColor = '#4CAF50';
                  e.target.style.transform = 'translateY(0)';
                }}
                disabled={loading}
              >
                {loading ? 'Saving...' : 'SAVE EMPLOYEE RIGHTS'}
              </button>
            </div>
          )}

          {/* No Employee Selected State */}
          {!userId && !loading && (
            <div style={{
              textAlign: 'center',
              padding: '60px',
              color: '#6b7280',
              backgroundColor: '#f8fafc',
              borderRadius: '8px',
              border: '2px dashed #d1d5db'
            }}>
              <h3 style={{ color: '#374151', marginBottom: '8px' }}>Select an Employee</h3>
              <p style={{ margin: 0 }}>
                Please select an employee email from the dropdown above to assign page access rights.
              </p>
            </div>
          )}
        </form>
      </div>
    </div>
  );
};

export default EmployeeRight;